import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { 
  Heart, Mail, Phone, Euro, Loader2, Filter, ChevronDown, ChevronUp, 
  Clock, CheckCircle, MessageCircle, XCircle, Save
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api, { adminInterestsAPI } from '../../lib/api';

const urgencies = [
  { value: 'all', label: 'Todas las urgencias' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low', label: 'Baja' },
];

const statuses = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'contacted', label: 'Contactado' },
  { value: 'in_progress', label: 'En proceso' },
  { value: 'closed', label: 'Cerrado' },
];

const positionColors = {
  GK: 'bg-yellow-500 text-yellow-950',
  DEF: 'bg-blue-500 text-white',
  MID: 'bg-green-500 text-white',
  FWD: 'bg-red-500 text-white',
};

const positionLabels = {
  GK: 'Portera',
  DEF: 'Defensa',
  MID: 'Mediocampista',
  FWD: 'Delantera',
};

const urgencyColors = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

const urgencyLabels = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
};

const statusIcons = {
  pending: Clock,
  contacted: MessageCircle,
  in_progress: CheckCircle,
  closed: XCircle,
};

const statusLabels = {
  pending: 'Pendiente',
  contacted: 'Contactado',
  in_progress: 'En proceso',
  closed: 'Cerrado',
};

const statusColors = {
  pending: 'text-amber-600 bg-amber-50',
  contacted: 'text-blue-600 bg-blue-50',
  in_progress: 'text-indigo-600 bg-indigo-50',
  closed: 'text-slate-600 bg-slate-100',
};

const InterestRow = ({ interest, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [editStatus, setEditStatus] = useState(interest.status);
  const [editNotes, setEditNotes] = useState(interest.admin_notes || '');
  const [saving, setSaving] = useState(false);

  const StatusIcon = statusIcons[interest.status];

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminInterestsAPI.update(interest.id, {
        status: editStatus,
        admin_notes: editNotes,
      });
      toast.success('Interés actualizado');
      onUpdate();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <tr 
        className="hover:bg-slate-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
            <div>
              <p className="font-semibold text-slate-800">{interest.player_name}</p>
              {interest.player_current_club && (
                <p className="text-sm text-slate-500">{interest.player_current_club}</p>
              )}
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-heading font-bold text-sm ${positionColors[interest.position]}`}>
            {interest.position}
          </span>
        </td>
        <td className="px-6 py-4">
          <div>
            <p className="font-semibold text-slate-800">{interest.club_name}</p>
            <p className="text-sm text-slate-500">{interest.club_league}</p>
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="space-y-1">
            <a href={`mailto:${interest.club_contact_email}`} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700">
              <Mail className="w-3 h-3" />
              {interest.club_contact_email}
            </a>
            {interest.club_contact_phone && (
              <a href={`tel:${interest.club_contact_phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800">
                <Phone className="w-3 h-3" />
                {interest.club_contact_phone}
              </a>
            )}
          </div>
        </td>
        <td className="px-6 py-4">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-bold border ${urgencyColors[interest.urgency]}`}>
            {urgencyLabels[interest.urgency]}
          </span>
        </td>
        <td className="px-6 py-4">
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${statusColors[interest.status]}`}>
            <StatusIcon className="w-3 h-3" />
            {statusLabels[interest.status]}
          </span>
        </td>
      </tr>
      
      {/* Expanded details row */}
      {expanded && (
        <tr className="bg-slate-50 border-t border-slate-100">
          <td colSpan={6} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Column 1: Operation details */}
              <div className="space-y-3">
                <h4 className="font-heading text-sm font-bold text-slate-600 uppercase tracking-wider">
                  Detalles de Operación
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tipo:</span>
                    <span className="font-medium text-slate-800">
                      {interest.operation_type === 'transfer' ? 'Transferencia' :
                       interest.operation_type === 'loan' ? 'Préstamo' : 'Ambos'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Presupuesto:</span>
                    <span className="font-medium text-slate-800">
                      {interest.budget 
                        ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(interest.budget)
                        : 'A consultar'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Salario máximo:</span>
                    <span className="font-medium text-slate-800">
                      {interest.max_salary 
                        ? `${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(interest.max_salary)}/año`
                        : 'A consultar'}
                    </span>
                  </div>
                </div>
                
                {interest.notes && (
                  <div className="mt-4">
                    <h5 className="text-xs font-semibold text-slate-500 uppercase mb-1">Notas del club:</h5>
                    <p className="text-sm text-slate-600 bg-white p-2 rounded border border-slate-200">
                      {interest.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Column 2: Contact info */}
              <div className="space-y-3">
                <h4 className="font-heading text-sm font-bold text-slate-600 uppercase tracking-wider">
                  Contacto del Club
                </h4>
                <div className="bg-white p-3 rounded-lg border border-slate-200 space-y-2">
                  <p className="font-medium text-slate-800">{interest.club_contact_name}</p>
                  <p className="text-sm text-slate-600">{interest.club_name}</p>
                  <div className="pt-2 space-y-1">
                    <a href={`mailto:${interest.club_contact_email}`} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700">
                      <Mail className="w-4 h-4" />
                      {interest.club_contact_email}
                    </a>
                    {interest.club_contact_phone && (
                      <a href={`tel:${interest.club_contact_phone}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900">
                        <Phone className="w-4 h-4" />
                        {interest.club_contact_phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Column 3: Admin actions */}
              <div className="space-y-3">
                <h4 className="font-heading text-sm font-bold text-slate-600 uppercase tracking-wider">
                  Gestión
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Estado</label>
                    <Select value={editStatus} onValueChange={setEditStatus}>
                      <SelectTrigger className="w-full" onClick={(e) => e.stopPropagation()}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="contacted">Contactado</SelectItem>
                        <SelectItem value="in_progress">En proceso</SelectItem>
                        <SelectItem value="closed">Cerrado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Notas internas</label>
                    <Textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Notas internas sobre este interés..."
                      rows={3}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <Button 
                    onClick={(e) => { e.stopPropagation(); handleSave(); }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Guardar Cambios
                  </Button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const AdminInterestsPage = () => {
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    urgency: 'all',
  });

  useEffect(() => {
    loadInterests();
  }, [filters]);

  const loadInterests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.urgency !== 'all') params.urgency = filters.urgency;
      
      const response = await api.get('/admin/interests', { params });
      setInterests(response.data);
    } catch (error) {
      console.error('Error loading interests:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ status: 'all', urgency: 'all' });
  };

  const pendingCount = interests.filter(i => i.status === 'pending').length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-interests-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 uppercase tracking-tight">
              Intereses en Jugadoras
            </h1>
            <p className="text-slate-500 mt-1">
              {interests.length} intereses recibidos
              {pendingCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                  {pendingCount} pendientes
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.urgency}
              onValueChange={(value) => setFilters({ ...filters, urgency: value })}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Urgencia" />
              </SelectTrigger>
              <SelectContent>
                {urgencies.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {u.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={clearFilters}>
              <Filter className="w-4 h-4 mr-2" />
              Limpiar
            </Button>
          </div>
        </div>

        {/* Interests Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Jugadora</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Pos</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Club Interesado</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Contacto</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Urgencia</th>
                    <th className="text-left px-6 py-4 font-heading text-xs uppercase tracking-wider text-slate-500">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {interests.map((interest) => (
                    <InterestRow key={interest.id} interest={interest} onUpdate={loadInterests} />
                  ))}
                </tbody>
              </table>
            </div>

            {interests.length === 0 && (
              <div className="p-12 text-center">
                <Heart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay intereses registrados</p>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminInterestsPage;
